/**
 * Regular rail component for displaying content in horizontal scrolling lists.
 * Renders different card types based on the rail's display mode and content type.
 * Supports various display modes including photo, poster, character, square, and addon cards.
 */

import Addon from '../../../../../../shared/classes/addon';
import AddonHorizontalCard from '../../../../elements/card/addonHorizontalCard';
import CharacterCard from '@components/elements/card/characterCard';
import CleanHorizontalCard from '../../../../elements/card/cleanHorizontalCard';
import Content from '../../../../../../shared/types/enums/content';
import EpgEvent from '../../../../../../shared/classes/epgEvent';
import EventCard from '@components/elements/card/eventCard';
import HorizontalCard from '../../../../elements/card/horizontalCard';
import IACorner from '@shared/classes/iacorner';
import List from '../../list';
import RailProps from '../../../../../../shared/types/props/rail';
import RegularEvent from '../../../../../../shared/classes/regularEvent';
import SquareCard from '../../../../elements/card/squareCard';
import VerticalCard from '../../../../elements/card/verticalCard';
import goToContent from '../../../../../../shared/actions/content/goToContent';
import { RailDisplayMode } from '../../../../../../shared/types/enums/rail';
import { store } from '@shared/store/store';

/**
 * Renders a regular rail.
 * Selects the appropriate card component based on display mode and content type.
 *
 * @param {RailProps} props - Rail props containing data, display mode, and event handlers
 * @returns {JSX.Element} Regular rail component with list of content cards
 */
function RegularRail({ list }: RailProps) {
  const { data, displayMode, onClick, focus: listFocus } = list;

  return (
    <List {...list}>
      {(data as (EpgEvent | RegularEvent | undefined)[])?.map(
        (item: Addon | EpgEvent | RegularEvent | undefined, index: number) => {
          if (!item) return;
          const goAction = (
            item: Addon | EpgEvent | RegularEvent | undefined
          ) => {
            const { focus: focusSlice } = store.getState();
            const focusArray = focusSlice.focus as number[];
            if (focusArray.length && focusArray.includes(list.focus)) {
              goToContent(item);
            }
          };
          const props = {
            item: item,
            index: index,
            onClick: () => goAction(item),
            releaseAction: () => goAction(item),
          };
          switch (displayMode) {
            case RailDisplayMode.ADDON_PHOTO:
              return <AddonHorizontalCard key={index} {...props} />;

            case RailDisplayMode.CLEAN_PHOTO:
              return <CleanHorizontalCard key={index} {...props} />;

            case RailDisplayMode.CHARACTER:
              return (
                <CharacterCard
                  {...props}
                  item={item as unknown as IACorner}
                  key={index}
                />
              );
              break;

            case RailDisplayMode.CHANNEL_GUIDE_PHOTO:
            case RailDisplayMode.PHOTO:
            case RailDisplayMode.RANKING_PHOTO:
              if (item?.type === Content.EPG_EVENT) {
                return (
                  <EventCard
                    key={index}
                    item={item as EpgEvent}
                    index={index}
                    onClick={() => onClick(item, index, listFocus)}
                    releaseAction={() => goToContent(item)}
                  />
                );
              }
              if (item?.type === Content.SERIES) {
                return (
                  <HorizontalCard
                    key={index}
                    showMask={false}
                    showTitle={false}
                    {...props}
                  />
                );
              }
              if (
                item?.seasonNumber &&
                ![Content.SEASON, Content.SHOW].includes(item.type)
              ) {
                return (
                  <HorizontalCard
                    key={index}
                    {...props}
                    showTitle={(item as RegularEvent)?.showTitle ?? true}
                  />
                );
              }
              return (
                <HorizontalCard
                  key={index}
                  {...props}
                  showTitle={(item as RegularEvent)?.showTitle ?? true}
                />
              );

            case RailDisplayMode.POSTER:
            case RailDisplayMode.RANKING_POSTER:
              return <VerticalCard key={index} {...props} />;

            case RailDisplayMode.SQUARE:
              return <SquareCard key={index} {...props} />;
          }
        }
      )}
    </List>
  );
}

export default RegularRail;
